import React, { lazy, Suspense, useEffect } from "react";
import { useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import AuthModal from "../../components-common/auth/AuthModal";
import { LoadingSpinnerCentered } from "../../components-common/LoadingSpinner";
import { lazyImportWrapper } from "../../utils/lazy-loading";
import { useWatchCurrentUserForErrorReporting } from "../../utils/errorReporting";
import {
  initializeMarketingAnalytics,
  trackMarketingClick,
  trackMarketingRoute
} from "./analytics";
import { MARKETING_PATHS } from "./config";
import { COMPARE_PATH_PREFIX } from "./compare/comparePages";

const HomePage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "HomePage" */ "./home/HomePage"))
);
const ProPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "ProPage" */ "./pro/ProPage"))
);
const MarketingHome = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "MarketingHome" */ "./old/MarketingHome"))
);
const MarketingPreviewPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "MarketingPreviewPage" */ "./old/MarketingPreviewPage")
  )
);
const FeaturesPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "FeaturesPage" */ "./pages/FeaturesPage"))
);
const FeatureCoursesPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "FeatureCoursesPage" */ "./pages/FeatureCoursesPage")
  )
);
const FeatureFeedPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "FeatureFeedPage" */ "./pages/FeatureFeedPage")
  )
);
const FeatureLiveCallPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "FeatureLiveCallPage" */ "./pages/FeatureLiveCallPage")
  )
);
const PricingPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "PricingPage" */ "./pages/PricingPage"))
);
const BookDemoPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "BookDemoPage" */ "./pages/BookDemoPage"))
);
const PrivacyPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "PrivacyPage" */ "./pages/PrivacyPage"))
);
const TermsPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "TermsPage" */ "./pages/TermsPage"))
);
const SubscriptionTermsPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "SubscriptionTermsPage" */ "./pages/SubscriptionTermsPage")
  )
);
const ProSlaPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "ProSlaPage" */ "./pages/ProSlaPage"))
);
const HelpPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "HelpPage" */ "./pages/HelpPage"))
);
const ComparePage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "ComparePage" */ "./compare/ComparePage"))
);
const EstimatePage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "EstimatePage" */ "./estimate/EstimatePage"))
);

export function MarketingScrollRestoration() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
}

export function MarketingAnalytics() {
  const fathomAnalytics = useSelector(state => state.frontend.platformData.fathomAnalytics);
  const isAdmin = useSelector(state => state.auth.currentUser?.role === "admin");
  const { pathname } = useLocation();

  useEffect(() => {
    initializeMarketingAnalytics(fathomAnalytics);
  }, [fathomAnalytics]);

  useEffect(() => {
    trackMarketingRoute(pathname);
  }, [pathname]);

  useEffect(() => {
    const handleClick = event => trackMarketingClick(event, { isAdmin });
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isAdmin]);

  return null;
}

export default function MarketingApp() {
  const location = useLocation();
  useWatchCurrentUserForErrorReporting();

  return (
    <>
      <MarketingAnalytics />
      <MarketingScrollRestoration />
      <Suspense fallback={<LoadingSpinnerCentered />}>
        <Switch location={location}>
          <Route exact path={MARKETING_PATHS.FEATURES} component={FeaturesPage} />
          <Route exact path={MARKETING_PATHS.COURSES} component={FeatureCoursesPage} />
          <Route exact path={MARKETING_PATHS.FEED} component={FeatureFeedPage} />
          <Route exact path={MARKETING_PATHS.LIVE_CALL} component={FeatureLiveCallPage} />
          <Redirect exact from={MARKETING_PATHS.LEGACY_PAYMENTS} to={MARKETING_PATHS.FEATURES} />
          <Route exact path={MARKETING_PATHS.PRICING} component={PricingPage} />
          <Route exact path={MARKETING_PATHS.BOOK_DEMO} component={BookDemoPage} />
          <Route exact path={MARKETING_PATHS.PRIVACY} component={PrivacyPage} />
          <Route exact path={MARKETING_PATHS.TERMS} component={TermsPage} />
          <Route
            exact
            path={MARKETING_PATHS.SUBSCRIPTION_TERMS}
            component={SubscriptionTermsPage}
          />
          <Route exact path={MARKETING_PATHS.PRO_SLA} component={ProSlaPage} />
          <Route exact path={MARKETING_PATHS.HELP} component={HelpPage} />
          <Route exact path={MARKETING_PATHS.HOME_PREVIEW} component={MarketingHome} />
          <Route exact path={MARKETING_PATHS.MARKETING_PREVIEW} component={MarketingPreviewPage} />
          <Route exact path={MARKETING_PATHS.ESTIMATE} component={EstimatePage} />
          <Route exact path={`${COMPARE_PATH_PREFIX}:slug`} component={ComparePage} />
          <Route exact path={MARKETING_PATHS.PRO} component={ProPage} />
          <Route exact path={MARKETING_PATHS.HOME} component={HomePage} />
          <Redirect to={MARKETING_PATHS.HOME} />
        </Switch>
      </Suspense>
      <AuthModal />
    </>
  );
}
